import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import promptService from '../services/promptService';
import ReactMarkdown from 'react-markdown';

const NewPromptFragment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('');
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(false);
  const [state, setState] = useState({ tags: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fragmentData, typesData] = await Promise.all([
          isEdit ? promptService.getFragmentById(id) : null,
          promptService.getAllFragmentTypes()
        ]);

        setTypes(typesData);

        if (isEdit && fragmentData) {
          setCode(fragmentData.code || '');
          setDescription(fragmentData.description || '');
          setContent(fragmentData.content || '');
          setType(fragmentData.type?.id || '');
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleSubmit = async () => {
    try {
      const fragmentData = {
        code,
        description,
        content,
        type: { id: type }
      };

      if (isEdit) {
        await promptService.updateFragment(id, fragmentData);
      } else {
        await promptService.createFragment(fragmentData);
      }
      
      navigate('/prompts?tab=fragments');
    } catch (error) {
      console.error('Error saving fragment:', error);
      setError('Failed to save fragment');
    }
  };

  const handleCancel = () => {
    navigate('/prompts?tab=fragments');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(content);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box id="new-prompt-fragment" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,}}>
        <Typography variant="h5">
          {isEdit ? 'Modifica Frammento' : 'Nuovo Frammento'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button onClick={handleCancel}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!code.trim() || !content.trim()}
          >
            Salva
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TextField
          fullWidth
          label="Codice"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={isEdit}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Descrizione"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            label="Tipo"
          >
            <MenuItem value="">Seleziona un tipo</MenuItem>
            {types.map((typeObj) => (
              <MenuItem key={typeObj.id} value={typeObj.id}>
                {typeObj.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Contenuto"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          rows={10}
          sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%' }, '& .MuiInputBase-input': { height: '100% !important' } }}
        />
      </Paper>

      <Dialog
        open={previewContent}
        onClose={() => setPreviewContent(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Anteprima Contenuto</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            mt: 2,
            '& ul': { 
              pl: 2,
              '& li': {
                mb: 1
              }
            },
            '& ol': {
              pl: 2,
              '& li': {
                mb: 1
              }
            },
            '& p': {
              mb: 2
            }
          }}>
            <ReactMarkdown>
              {content}
            </ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CopyIcon />} onClick={handleCopyContent}>
            Copia del frammento
          </Button>
          <Button onClick={() => setPreviewContent(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewPromptFragment; 